import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { usePersistenceContext } from "@/shared/lib/persistence-state/context";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";
import t from "@/lang/lang";
import { toast } from "sonner";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useErrValidationFailedContext } from "@/shared/lib/errors/err-validation-failed-context";

export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay ?? 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * The tuple returned by an RTK Query mutation hook, e.g.
 * `const mutation = useRltWorkOrderUpdateTeamLeaderMutation()`.
 *
 * Only the members the debounce module relies on are described here so the
 * concrete (and far more detailed) RTK Query type is structurally assignable.
 * Passing the whole tuple lets the hook own the error/success/reset handling
 * instead of every call site wiring up the individual parts.
 */
export type RtkQueryMutation<Req, Res> = readonly [
  RtkQueryMutationTrigger<Req, Res>,
  RtkQueryMutationResult<Res>,
];

/**
 * The trigger function of an RTK Query mutation hook. The real return value is
 * an RTK `MutationActionCreatorResult`; we only require `.unwrap()` so the
 * promise can be awaited and its error caught.
 */
export type RtkQueryMutationTrigger<Req, Res> = (request: Req) => {
  unwrap: () => Promise<Res>;
};

/**
 * The result object of an RTK Query mutation hook. Structurally a subset of the
 * actual RTK type, which is therefore assignable to it.
 */
export interface RtkQueryMutationResult<Res> {
  data?: Res;
  error?: unknown;
  isLoading: boolean;
  isSuccess: boolean;
  reset: () => void;
}

/**
 * Options for {@link useDebouncedMutation}. All fields have sensible defaults;
 * callers pass a partial object to override individual behaviours.
 */
export interface DebouncedMutationOptions<Req, Res> {
  /** Debounce delay in milliseconds before the mutation is sent. */
  delay: number;
  /**
   * When true no mutation is sent. Use for invalid input or disabled states.
   */
  skip: boolean;
  /**
   * When true (default) the local request state is reset to `resetState` (the
   * current server / cache value) after a failed mutation.
   *
   * Set to false when the call site renders inline field validation from the
   * returned `error` instead (resetting would clear both the value and error).
   */
  resetOnError: boolean;
  /** When true (default) a toast is shown on error. */
  toastError: boolean;
  /** When true a toast is shown on success. Defaults to false. */
  toastSuccess: boolean;
  toastSuccessTitle?: string;
  toastSuccessDescription?: string;
  /**
   * When true (default) the surrounding persistence context is updated
   * (SAVING / SAVED / ERROR) to drive the global "saving…" indicator.
   */
  updatePersistenceState: boolean;
  /**
   * When true (default) the field errors of a failed mutation — a validation
   * error (422) or a document not ready error — are written into the nearest
   * ErrValidationFailedContextProvider, where input components given a
   * `fieldName` highlight them. The hook never resets errors of the context;
   * the input components do so when the user changes the value.
   *
   * Set to false when the mutation edits another entity than the one of the
   * surrounding context and a nested provider is not an option.
   */
  writeToErrValidationFailedContext: boolean;
  /** Called after a successful mutation with the unwrapped result. */
  onSuccess?: (result: Res, request: Req) => void;
  /** Called after a failed mutation with the raw error. */
  onError?: (error: unknown, request: Req) => void;
}

const defaultDebouncedMutationOptions = {
  delay: 250,
  skip: false,
  resetOnError: true,
  toastError: true,
  toastSuccess: false,
  updatePersistenceState: true,
  writeToErrValidationFailedContext: true,
} satisfies Partial<DebouncedMutationOptions<unknown, unknown>>;

/**
 * Debounced mutation hook with built-in error recovery.
 *
 * Owns the full save lifecycle for a locally edited request object:
 * - debounces `request` and sends it through the RTK Query `mutation`;
 * - on error shows a toast and (by default) resets the local state back to
 *   `resetState` — the current server value — so the field never lingers with
 *   a value the backend rejected;
 * - drives the persistence context (SAVING / SAVED / ERROR) for the global
 *   "saving…" indicator;
 * - writes the field errors of a failed save into the nearest
 *   ErrValidationFailedContextProvider (see `writeToErrValidationFailedContext`).
 *
 * Firing is governed purely by *reference identity*, not by any value-based
 * "dirty" comparison: the hook tracks the request reference it last considered
 * persisted (`syncedRef`) and sends a mutation only when the debounced request
 * is a different reference. That reference starts at the initial `request`
 * (so the mount value is skipped) and advances on a successful save and on an
 * error reset (so the reset does not trigger a redundant save). A genuine edit
 * via `setRequest({ ...request, field })` is a new reference and therefore
 * fires.
 *
 * Re-hydrating local state when the server entity changes (e.g. via
 * `useRefEffect` + `setRequest`) is the caller's responsibility and behaves as
 * it always has: the new reference is treated as an edit. The hook deliberately
 * does not try to guess which programmatic state changes should be suppressed.
 *
 * @param request      the local, editable copy of the entity
 * @param setRequest   the setter for `request` (used to reset after an error)
 * @param mutation     the full RTK Query mutation tuple, e.g. `useFooMutation()`
 * @param resetState   the current server / cache value to reset to on error
 * @param options      partial overrides of {@link DebouncedMutationOptions}
 * @returns `{ isLoading, error }` from the underlying mutation
 */
export function useDebouncedMutation<Req, Res>(
  request: Req,
  setRequest: Dispatch<SetStateAction<Req>>,
  mutation: RtkQueryMutation<Req, Res>,
  resetState: Req,
  options?: Partial<DebouncedMutationOptions<Req, Res>>,
): { isLoading: boolean; error: unknown } {
  const opts = { ...defaultDebouncedMutationOptions, ...options };

  const [trigger, { isLoading, error, reset }] = mutation;

  // The request reference currently believed to be persisted on the server.
  // A mutation is sent only when the debounced request is a *different*
  // reference. This is a pure identity guard — it never compares values, so it
  // makes no assumption about how to detect a changed field and cannot go
  // stale. It advances to the submitted value on success and to `resetState`
  // on an error reset; it starts at the initial request, so mount is skipped.
  const syncedRef = useRef<Req>(request);

  // Mirror the latest props / options / mutation handles into refs so the
  // async mutation callback can read current values without making them effect
  // dependencies. This is essential: the mutation hook lives in the calling
  // component, so its state changes (isLoading, error) re-render the component
  // and can change `trigger` / `reset` identity. If those were dependencies,
  // the fire effect would re-run mid-flight, its cleanup would flip `active`,
  // and the in-flight save's error handler would skip the reset. The effect
  // must re-run only when a *newer* debounced value supersedes the current one.
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;
  const resetMutationRef = useRef(reset);
  resetMutationRef.current = reset;
  const resetStateRef = useRef(resetState);
  resetStateRef.current = resetState;
  const setRequestRef = useRef(setRequest);
  setRequestRef.current = setRequest;
  const requestRef = useRef(request);
  requestRef.current = request;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const { setPersistenceState } = usePersistenceContext();
  const setPersistenceStateRef = useRef(setPersistenceState);
  setPersistenceStateRef.current = setPersistenceState;

  const { write: writeValidationError } = useErrValidationFailedContext();
  const writeValidationErrorRef = useRef(writeValidationError);
  writeValidationErrorRef.current = writeValidationError;

  // Reflect pending edits in the persistence indicator immediately, before the
  // debounce elapses, so the indicator does not lag behind the user.
  useEffect(() => {
    const o = optsRef.current;
    if (o.skip || !o.updatePersistenceState) {
      return;
    }
    if (request !== syncedRef.current) {
      setPersistenceStateRef.current(PersistenceState.SAVING);
    }
  }, [request]);

  const debouncedRequest = useDebounce(request, opts.delay);

  useEffect(() => {
    const o = optsRef.current;
    if (o.skip || debouncedRequest === syncedRef.current) {
      return;
    }

    const submitted = debouncedRequest;
    // Guards against a superseded in-flight save writing back a stale synced
    // reference or resetting state the user has already moved past. The effect
    // re-runs (and so this cleanup flips `active`) only when `debouncedRequest`
    // changes, i.e. a genuinely newer value — never on unrelated re-renders.
    let active = true;

    triggerRef
      .current(submitted)
      .unwrap()
      .then((result) => {
        if (!active || requestRef.current !== submitted) {
          return;
        }
        syncedRef.current = submitted;
        if (o.updatePersistenceState) {
          setPersistenceStateRef.current(PersistenceState.SAVED);
        }
        if (o.toastSuccess) {
          toast(o.toastSuccessTitle ?? t("Operation Erfolgreich!"), {
            description: o.toastSuccessDescription ?? "",
          });
        }
        o.onSuccess?.(result, submitted);
      })
      .catch((err) => {
        if (!active || requestRef.current !== submitted) {
          return;
        }
        if (o.writeToErrValidationFailedContext) {
          writeValidationErrorRef.current(err);
        }
        if (o.toastError) {
          toast(...parseRTKQueryErrorToSonner(err, false));
        }
        if (o.resetOnError) {
          const serverState = resetStateRef.current;
          syncedRef.current = serverState;
          setRequestRef.current(serverState);
          resetMutationRef.current();
          if (o.updatePersistenceState) {
            setPersistenceStateRef.current(PersistenceState.SAVED);
          }
        } else if (o.updatePersistenceState) {
          setPersistenceStateRef.current(PersistenceState.ERROR);
        }
        o.onError?.(err, submitted);
      });

    return () => {
      active = false;
    };
    // Intentionally depends only on `debouncedRequest`: the effect must fire a
    // save (and cancel the prior one) exactly when a newer debounced value
    // arrives. `trigger` / `reset` / `skip` are read through refs so unrelated
    // re-renders cannot cancel an in-flight save's success/error handling.
  }, [debouncedRequest]);

  return { isLoading, error };
}

function useDebouncedMutationInternal<T>(
  request: T,
  mutation: (request: T) => void,
  mutationIsLoading: boolean,
  mutationError: unknown,
  mutationIsSuccess: boolean,
  mutationReset: () => void,
  delay: number,
  options: Options = { skip: false },
) {
  // The useDebounceMutation hook will run on the first render of the component.
  // The request will have some state at this point. However, no user-action has
  // taken place yet. Therefore, there is no need to run the mutation for this
  // initial state. Keep in mind: The useState hook will not update the initialRequest
  // on re-renders of useDebounceMutation.
  const [initialRequest] = useState<T>(request);

  const [persistenceState, setPersistenceState] = useState<PersistenceState>(
    PersistenceState.SAVED,
  );

  // The persistence state is set so saving even though
  // the debouncedMutation is not updated yet and the
  // save process has not started yet. However,
  // an intermediate state like "edited" has little value
  // if only shown for less than a second. It would just
  // cause the UI to flicker around the different states.
  useEffect(() => {
    if (options.skip) {
      return;
    }
    if (request !== initialRequest) {
      setPersistenceState(PersistenceState.SAVING);
    }
  }, [initialRequest, options.skip, request]);

  // Debounce the request to avoid too many requests.
  const debouncedRequest = useDebounce(request, delay);

  // Execute the mutation when the debounced request changes.
  useEffect(() => {
    // Prevent the unnecessary execution of the mutation for the
    // initial state. See comment above.
    if (debouncedRequest === initialRequest) {
      return;
    }
    if (options.skip) {
      // If the skip option is set, do not execute the mutation.
      // This is useful for cases where the request is not valid
      // or the mutation should not be executed.
      return;
    }
    mutation(debouncedRequest);
  }, [debouncedRequest, initialRequest, mutation, options.skip]);

  // Update the persistence status based on the save state
  // useEffect is needed to avoid an infinite loop -> mutation cannot be reset here
  useEffect(() => {
    // The second predicate avoids unnecessary re-renders. The state
    // may already be set to SAVING in the updateCalculation function.
    if (mutationIsLoading && persistenceState !== PersistenceState.SAVING) {
      setPersistenceState(PersistenceState.SAVING);
      return;
    }
    if (mutationError && persistenceState !== PersistenceState.ERROR) {
      setPersistenceState(PersistenceState.ERROR);
    }
  }, [mutationIsLoading, mutationError, persistenceState]);

  // Reset the persistence status and mutation when the mutation is successful
  // The useEffect is added to allow clients to pass a decorated reset function
  // that may have additional side effects.
  useEffect(() => {
    if (mutationIsSuccess) {
      setPersistenceState(PersistenceState.SAVED);
      mutationReset();
    }
  }, [mutationIsSuccess, mutationReset]);

  // Show a toast when an error occurs.
  useEffect(() => {
    if (options?.toastError && mutationError) {
      toast(...parseRTKQueryErrorToSonner(mutationError, false));
      mutationReset();
    }
    if (options?.toastSuccess && mutationIsSuccess) {
      toast(options?.toastSuccessTitle || t("Operation Erfolgreich!"), {
        description: options?.toastSuccessDescription || "",
      });
      mutationReset();
    }
  }, [
    mutationError,
    mutationIsSuccess,
    mutationReset,
    options?.toastError,
    options?.toastSuccess,
    options?.toastSuccessDescription,
    options?.toastSuccessTitle,
  ]);

  return { persistenceState };
}

/**
 * Interface for the options of the
 * useDebouncedMutationWithPersistenceStateContextUpdate hook.
 */
export interface Options {
  /**
   * The delay for the debounce to use.
   * Ignored when using useDebounceMutationWithPersistenceStateContextUpdate.
   */
  delay?: number;
  /**
   * If set to true, a toast will be shown when an error occurs.
   * This will also reset the mutation.
   */
  toastError?: boolean;
  /**
   * If set to true, a toast will be shown when the mutation
   * is successful. This will also reset the mutation.
   */
  toastSuccess?: boolean;
  toastSuccessTitle?: string;
  toastSuccessDescription?: string;
  /**
   * If set to true, the mutation will be skipped.
   * This is useful for cases where the mutation should not
   * be executed, e.g. when the request is not valid.
   */
  skip?: boolean;
}

export function useDebouncedMutationWithPersistenceStateContextUpdate<T>(
  request: T,
  mutation: (request: T) => void,
  mutationIsLoading: boolean,
  mutationError: unknown,
  mutationIsSuccess: boolean,
  mutationReset: () => void,
  delay: number,
  options: Options = { toastError: false, toastSuccess: false, skip: false },
) {
  const { persistenceState } = useDebouncedMutationInternal(
    request,
    mutation,
    mutationIsLoading,
    mutationError,
    mutationIsSuccess,
    mutationReset,
    delay,
    options,
  );

  const { setPersistenceState } = usePersistenceContext();
  useEffect(() => {
    setPersistenceState(persistenceState);
  }, [setPersistenceState, persistenceState]);

  return { persistenceState };
}

export interface DebouncedDirtyOptions<T> extends Options {
  /**
   * Controls if the current request should be considered "dirty".
   *
   * Why this exists:
   * Some UIs keep a local editable copy of server data. During the initial
   * hydration (or when switching to a different entity), that local request
   * object is often recreated even though the values didn't change.
   * The default debounced hook uses reference equality against the initial
   * request and would treat that recreation as a change, triggering an
   * unnecessary mutation.
   *
   * With this option you can explicitly define what "changed" means.
   */
  isDirty: (request: T, baseline: T) => boolean;
  /**
   * Optional key that identifies the logical "entity" represented by the request.
   * When it changes, the baseline snapshot is reset to the current request.
   *
   * Prefer a stable scalar (string/number) so hook dependencies can be
   * statically verified by eslint.
   */
  baselineKey?: string | number;
}

/**
 * Debounced mutation hook variant for complex request objects.
 *
 * Use this variant when the request object may be recreated during hydration
 * or other non-user-driven updates, and you want to prevent unnecessary
 * mutations by providing an explicit dirty check.
 */
export function useDebouncedDirtyMutationWithPersistenceStateContextUpdate<T>(
  request: T,
  mutation: (request: T) => void,
  mutationIsLoading: boolean,
  mutationError: unknown,
  mutationIsSuccess: boolean,
  mutationReset: () => void,
  delay: number,
  options: DebouncedDirtyOptions<T>,
) {
  const { setPersistenceState } = usePersistenceContext();

  const [baseline, setBaseline] = useState<T>(request);
  const [lastSubmittedRequest, setLastSubmittedRequest] = useState<T>(request);
  const currentRequestRef = useRef(request);

  useEffect(() => {
    currentRequestRef.current = request;
  }, [request]);

  const mutationWithSubmissionTracking = useCallback(
    (next: T) => {
      setLastSubmittedRequest(next);
      mutation(next);
    },
    [mutation],
  );

  // Reset baseline when the caller indicates the request context changed.
  useEffect(() => {
    if (options.baselineKey === undefined) {
      return;
    }
    setBaseline(currentRequestRef.current);
  }, [options.baselineKey]);

  // Keep baseline in sync with successful saves.
  useEffect(() => {
    if (mutationIsSuccess) {
      setBaseline(lastSubmittedRequest);
    }
  }, [lastSubmittedRequest, mutationIsSuccess]);

  const isDirty = options.isDirty(request, baseline);

  const { persistenceState } = useDebouncedMutationInternal(
    request,
    mutationWithSubmissionTracking,
    mutationIsLoading,
    mutationError,
    mutationIsSuccess,
    mutationReset,
    delay,
    {
      ...options,
      skip: options.skip || !isDirty,
    },
  );

  useEffect(() => {
    setPersistenceState(persistenceState);
  }, [persistenceState, setPersistenceState]);

  return { persistenceState };
}
